import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsPaperclip } from "react-icons/bs";
import styles from "../../css/messages.module.css";
import { useFiles } from "../../../../hooks/useFiles";

export const MessageInput = ({
  groupName,
  handleSendMessage,
  isUploading,
  uploadProgress,
}) => {
  const { t, i18n } = useTranslation();
  const { getFileIcon, formatFileSize } = useFiles();
  const [inputRows, setInputRows] = useState(1);
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);

  // Clear message input and set typing rows to 1 when changing the activeGroup
  useEffect(() => {
    setNewMessage("");
    setInputRows(1);
    setFile(null);
  }, [groupName]);

  const sendMessage = async () => {
    const sent = await handleSendMessage(newMessage, file);

    // clear the input and set input rows to 1 if successfully sent
    if (sent) {
      setNewMessage("");
      setInputRows(1);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSendMessagePress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleMessageChange = (value) => {
    setNewMessage(value);

    // Calculate current number of lines
    const lineCount = value.split("\n").length;
    const newRows = Math.min(Math.max(lineCount, 1), 10); // Between 1 and 10

    setInputRows(newRows);
  };

  // File selection handler
  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    // File size validation
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (selectedFile.size > MAX_FILE_SIZE) {
      alert(t("admin_pages.messages.file_too_large"));
      return;
    }

    setFile(selectedFile);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();

    // Focus on the text area after getting the file
    textAreaRef.current.focus();
  };

  // Remove attached file
  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderFilePreview = (file) => {
    return (
      <div className={styles.fileMessageContainer}>
        <div className={styles.fileMessageContent}>
          {getFileIcon(file.type, file.name)}
          <div className={styles.fileMessageInfo}>
            <span className={styles.fileMessageName}>{file.name}</span>
            {file.size && (
              <span className={styles.fileMessageSize}>
                {formatFileSize(file.size)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.messageInputContainer}>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleFileSelect(e.target.files?.[0])}
        className={styles.fileInput}
        style={{ display: "none" }} // Hidden but functional
      />

      {/* File preview if file exists */}
      {file && (
        <div className={styles.filePreview}>
          {renderFilePreview(file)}
          <span onClick={removeFile} className={styles.removeFileButton}>
            ×
          </span>
        </div>
      )}

      {/* Upload progress if uploading */}
      {isUploading && (
        <div className={styles.uploadProgress}>
          <div
            className={styles.uploadProgressBar}
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      <div className={styles.messageInputWrapper}>
        {/* Attachment button */}
        <button
          onClick={handleAttachClick}
          className={styles.attachButton}
          type="button"
        >
          <BsPaperclip size={20} />
        </button>

        {/* Textarea */}
        <textarea
          ref={textAreaRef}
          value={newMessage}
          onChange={(e) => handleMessageChange(e.target.value)}
          onKeyDown={handleSendMessagePress}
          placeholder={t("admin_pages.messages.type_message")}
          className={styles.messageInput}
          rows={inputRows}
          disabled={isUploading}
        />
        <button
          onClick={sendMessage}
          disabled={(!newMessage?.trim() && !file) || isUploading}
          className={styles.sendButton}
        >
          {isUploading ? (
            <div className={styles.spinner}></div>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                transform: i18n.dir() === "rtl" ? "scaleX(-1)" : "none",
              }}
            >
              <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};
